import {
  Field, FieldProps, Form, Formik, FormikProps,
} from 'formik';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import { ETouchType } from 'types/touch-type';
import * as Yup from 'yup';

import { ButtonBase, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

import ActionModal from 'components/ActionModal/ActionModal';
import CustomSelect from 'components/CustomSelect/CustomSelect';
import FormikOnError from 'components/FormikOnError/FormikOnError';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { createCampaignExtraTouch } from 'utils/api';
import { enumToReadableName, formatCents, showAPIErrorAlert } from 'utils/lib';

import CustomTouchDateTimePicker from './CustomTouchDateTimePicker';
import { getMinimumSendAt } from './utils';

const useStyles = makeStyles((theme) => ({
  modalMessage: {
    fontSize: '15px',
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
  helperText: {
    color: theme.palette.darkGray.main,
    marginBottom: '24px',
  },
  fieldSpacer: {
    margin: '10px 0 30px',
  },
}));

interface TouchTypeOption {
  touchTypeId: string;
  touchTypeName: ETouchType;
}

interface FormValues {
  touchTypeId: string;
  sendAt: string;
}

interface Props {
  campaignId: string;
  touchTypeOptions: TouchTypeOption[];
  defaultSendAt?: string | null;
  campaignEndDate?: string | null;
  extraTouchPricePerApnCents?: number | null;
  onClose: any;
  onTouchCreated: any;
}

const AddExtraTouchModal = ({
  campaignId,
  touchTypeOptions,
  defaultSendAt,
  campaignEndDate,
  extraTouchPricePerApnCents,
  onClose,
  onTouchCreated,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const minimumSendAt = getMinimumSendAt();
  const maximumSendAt = campaignEndDate ? moment(campaignEndDate).subtract(1, 'days') : null;
  const initialTouchTypeId = touchTypeOptions[0]?.touchTypeId ?? '';
  let initialSendAtMoment = defaultSendAt && moment(defaultSendAt).isAfter(minimumSendAt)
    ? moment(defaultSendAt)
    : minimumSendAt.clone();

  if (maximumSendAt && initialSendAtMoment.isAfter(maximumSendAt)) {
    initialSendAtMoment = maximumSendAt.clone();
  }

  const initialSendAt = initialSendAtMoment.toISOString();

  return (
    <Formik<FormValues>
      initialValues={{
        touchTypeId: initialTouchTypeId,
        sendAt: initialSendAt,
      }}
      validationSchema={Yup.object().shape({
        touchTypeId: Yup.string().required('Touch type is required.'),
        sendAt: Yup.string()
          .required('Send date and time is required.')
          .test(
            'minimum-lead-time',
            'Extra touches must be scheduled at least 2 hours from now.',
            (value) => !!value && moment(value).isSameOrAfter(getMinimumSendAt()),
          )
          .test(
            'campaign-end-date',
            'Extra touches must be scheduled on or before the campaign end date.',
            (value) => !value || !maximumSendAt || moment(value).isSameOrBefore(maximumSendAt),
          ),
      })}
      onSubmit={async (values) => {
        showLoadingIndicatorModal();

        try {
          const { touchTrigger } = await createCampaignExtraTouch(campaignId, values.touchTypeId, moment(values.sendAt).toISOString());
          onTouchCreated(touchTrigger);
          onClose();
          toast.success('Extra touch scheduled. Customize it now to make sure it can be sent.');
        } catch (err) {
          console.error(err);
          showAPIErrorAlert(err);
        } finally {
          hideLoadingIndicatorModal();
        }
      }}
    >
      {({
        handleSubmit, isSubmitting,
      }: FormikProps<FormValues>) => (
        <Form>
          <FormikOnError>
            <ActionModal
              backgroundColor={theme.palette.offWhite.main}
              textColor={theme.palette.darkGray.main}
              icon={<AddCircleOutlineIcon />}
              onClose={onClose}
              title="Add Extra Touch"
              message={(
                <div className={classes.modalMessage}>
                  <div className={classes.helperText}>
                    Schedule an extra email, postcard, or letter to go out to your campaign. You'll customize the content right after scheduling.
                    <br />
                    <br />
                    Email extra touches are free. Letter and postcard extra touches are billed at your campaign's per APN rate
                    {extraTouchPricePerApnCents != null ? ` of ${formatCents(extraTouchPricePerApnCents)} per APN.` : '.'}
                  </div>

                  <Field name="touchTypeId">
                    {({ field, meta }: FieldProps) => (
                      <CustomSelect
                        labelText="Choose Touch Type"
                        items={touchTypeOptions.map((option) => ({
                          name: enumToReadableName(option.touchTypeName),
                          value: option.touchTypeId,
                        }))}
                        inputProps={field}
                        error={meta.touched && !!meta.error}
                      />
                    )}
                  </Field>

                  <div className={classes.fieldSpacer}>
                    <Field name="sendAt">
                      {({ field, form, meta }: FieldProps) => (
                        <CustomTouchDateTimePicker
                          name={field.name}
                          value={field.value}
                          onChange={(value) => form.setFieldValue(field.name, value)}
                          onClose={() => form.setFieldTouched(field.name, true, true)}
                          minDate={minimumSendAt}
                          maxDate={maximumSendAt ?? undefined}
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched ? meta.error : undefined}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              )}
              buttons={[
                <ButtonBase onClick={onClose} className={classes.cancelButton}>Cancel</ButtonBase>,
                <ButtonBase onClick={() => handleSubmit()} disabled={isSubmitting || touchTypeOptions.length === 0}>
                  Schedule Touch
                </ButtonBase>,
              ]}
            />
          </FormikOnError>
        </Form>
      )}
    </Formik>
  );
};

export default AddExtraTouchModal;
